// Admin — dedicated Marketplace Orders section
// Shows every marketplace order with: buyer, product, amount, vendor, status, timestamp.
// Admin can change status: Confirmed | Preparing | Packed | Shipped | Delivered | Rejected.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { RefreshCw, Search, Filter, ExternalLink, Package, CheckCircle2, XCircle } from 'lucide-react';
import api from '../../utils/api';

const STATUSES = [
  { key: 'pending',          label: 'Placed',      color: 'bg-amber-500/25 text-amber-300 border-amber-400/60',      dot: 'bg-amber-400' },
  { key: 'confirmed',        label: 'Confirmed',   color: 'bg-cyan-500/25 text-cyan-300 border-cyan-400/60',         dot: 'bg-cyan-400' },
  { key: 'preparing',        label: 'Preparing',   color: 'bg-sky-500/25 text-sky-300 border-sky-400/60',            dot: 'bg-sky-400' },
  { key: 'packed',           label: 'Packed',      color: 'bg-indigo-500/25 text-indigo-300 border-indigo-400/60',   dot: 'bg-indigo-400' },
  { key: 'shipped',          label: 'Shipped',     color: 'bg-blue-500/25 text-blue-300 border-blue-400/60',         dot: 'bg-blue-400' },
  { key: 'out_for_delivery', label: 'Out for Del.', color: 'bg-purple-500/25 text-purple-300 border-purple-400/60',  dot: 'bg-purple-400' },
  { key: 'delivered',        label: 'Delivered',   color: 'bg-emerald-500/25 text-emerald-300 border-emerald-400/60', dot: 'bg-emerald-400' },
  { key: 'rejected',         label: 'Rejected',    color: 'bg-rose-500/25 text-rose-300 border-rose-400/60',         dot: 'bg-rose-400' },
];

const STATUS_MAP = STATUSES.reduce((a, s) => (a[s.key] = s, a), {});

export default function AdminMarketplaceOrders() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [busyIds, setBusyIds] = useState({});
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set('days', String(days));
      params.set('limit', '300');
      if (filter) params.set('status', filter);
      if (search) params.set('search', search);
      const r = await api.get(`/admin/marketplace/orders?${params.toString()}`);
      setRows(r.data.orders || []);
      setCounts(r.data.counts_by_status || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load failed');
    } finally { setBusy(false); }
  }, [days, filter, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const updateStatus = async (orderId, newStatus, note) => {
    if (!window.confirm(`Change status to "${STATUS_MAP[newStatus]?.label || newStatus}"?`)) return;
    setBusyIds((p) => ({ ...p, [orderId]: true }));
    try {
      await api.post(`/marketplace/order/${orderId}/status`, { status: newStatus, note: note || undefined });
      toast.success(`✅ Order → ${STATUS_MAP[newStatus]?.label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusyIds((p) => ({ ...p, [orderId]: false }));
    }
  };

  const totalRevenue = useMemo(() =>
    rows.filter(o => o.status !== 'rejected').reduce((s, o) => s + Number(o.total_at || 0), 0)
  , [rows]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-white p-4 sm:p-6" data-testid="admin-mkt-orders-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              📦 Marketplace Orders
            </h1>
            <div className="text-[11px] opacity-70">
              {rows.length} orders shown · Total revenue: <b className="text-yellow-300">{totalRevenue.toFixed(0)} AT</b> · Auto-refresh {autoRefresh ? 'ON (8s)' : 'OFF'}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={() => setAutoRefresh(v => !v)} data-testid="mkt-orders-auto-toggle"
                    className={`px-3 py-1.5 rounded-lg text-xs font-black ${autoRefresh ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-white'}`}>
              {autoRefresh ? '⏱ Auto' : '⏱ Manual'}
            </button>
            <button onClick={load} disabled={busy} data-testid="mkt-orders-refresh"
                    className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black flex items-center gap-1">
              <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="mkt-orders-filters">
          <div className="flex items-center gap-1 bg-slate-900 rounded-lg px-2">
            <Search size={12} className="opacity-60" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Buyer name / phone / city / order-id"
                   data-testid="mkt-orders-search"
                   className="bg-transparent text-xs px-2 py-1.5 outline-none w-64" />
          </div>
          <div className="flex items-center gap-1 bg-slate-900 rounded-lg px-2">
            <Filter size={12} className="opacity-60" />
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                    data-testid="mkt-orders-days"
                    className="bg-transparent text-xs py-1.5 outline-none">
              <option value={7} className="bg-slate-900">Last 7 days</option>
              <option value={30} className="bg-slate-900">Last 30 days</option>
              <option value={90} className="bg-slate-900">Last 90 days</option>
              <option value={365} className="bg-slate-900">Last 1 year</option>
            </select>
          </div>
          <button onClick={() => setFilter('')} data-testid="mkt-orders-filter-all"
                  className={`px-2 py-1 rounded text-[10px] font-black ${!filter ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>
            All ({rows.length})
          </button>
          {STATUSES.map((s) => (
            <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`mkt-orders-filter-${s.key}`}
                    className={`px-2 py-1 rounded text-[10px] font-black border ${filter === s.key ? 'bg-purple-500 text-white border-purple-300' : s.color}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${s.dot}`} />
              {s.label} ({counts[s.key] || 0})
            </button>
          ))}
        </div>

        {/* Orders table */}
        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden" data-testid="mkt-orders-list">
          {rows.length === 0 && (
            <div className="p-8 text-center opacity-60 text-xs">
              {busy ? '⏳ Loading orders…' : 'No orders match filters.'}
            </div>
          )}
          <div className="divide-y divide-slate-800">
            {rows.map((o) => {
              const st = STATUS_MAP[o.status] || { label: o.status, color: 'bg-slate-600', dot: 'bg-slate-400' };
              const isBusy = !!busyIds[o.id];
              const isOpen = expanded === o.id;
              const created = new Date(o.created_at || Date.now());
              const vendors = o.vendors_summary || [];
              return (
                <div key={o.id} className="p-3 hover:bg-slate-800/40 transition" data-testid={`mkt-order-row-${o.id.slice(0,8)}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    {/* Buyer + Order-ID */}
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm font-black flex items-center gap-2">
                        <Package size={14} className="text-purple-400" />
                        {o.buyer_name || <span className="opacity-60 italic">—</span>}
                      </div>
                      <div className="text-[10px] opacity-70">
                        📞 {o.buyer_phone || '—'} · 📍 {o.buyer_city || '—'}
                      </div>
                      <div className="text-[10px] opacity-50 font-mono mt-0.5">#{o.id.slice(0, 8)}</div>
                    </div>

                    {/* Products */}
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-xs opacity-90 line-clamp-2" title={o.item_titles}>
                        🛍️ {o.item_titles || '—'}
                      </div>
                      <div className="text-[10px] opacity-60">{o.item_count} item{o.item_count !== 1 ? 's' : ''}</div>
                    </div>

                    {/* Vendors */}
                    <div className="flex-1 min-w-[140px]">
                      {vendors.map((v, i) => (
                        <div key={i} className="text-[11px]">
                          <span className="text-cyan-300 font-black">🏪 {v.shop_name}</span>
                          <span className="opacity-60 ml-1">· {v.amount.toFixed(0)} AT</span>
                        </div>
                      ))}
                    </div>

                    {/* Amount */}
                    <div className="text-right min-w-[80px]">
                      <div className="text-lg font-black text-yellow-300">{Number(o.total_at || 0).toFixed(0)} AT</div>
                      {o.delivery_at > 0 && <div className="text-[9px] opacity-60">+{Number(o.delivery_at).toFixed(0)} del</div>}
                    </div>

                    {/* Status pill */}
                    <div className="min-w-[100px] text-center">
                      <span className={`text-[10px] px-2 py-1 rounded-full border font-black uppercase ${st.color}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${st.dot} animate-pulse`} />
                        {st.label}
                      </span>
                    </div>

                    {/* Timestamp */}
                    <div className="text-[10px] opacity-60 min-w-[100px] text-right">
                      {created.toLocaleDateString('en-IN')}<br />
                      {created.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>

                  {/* Actions row */}
                  <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-800">
                    {STATUSES.filter(s => s.key !== o.status).map((s) => (
                      <button key={s.key} disabled={isBusy}
                              onClick={() => updateStatus(o.id, s.key)}
                              data-testid={`mkt-order-${o.id.slice(0,8)}-${s.key}`}
                              className={`text-[10px] px-2 py-1 rounded font-black border transition ${
                                s.key === 'rejected'
                                  ? 'bg-rose-600/20 border-rose-500/40 text-rose-300 hover:bg-rose-600/40'
                                  : 'bg-slate-800 border-slate-700 hover:bg-purple-600 hover:border-purple-400'
                              } disabled:opacity-40`}>
                        {s.key === 'rejected' ? '✗' : '→'} {s.label}
                      </button>
                    ))}
                    <a href={`/order/${o.id}`} target="_blank" rel="noopener noreferrer"
                       data-testid={`mkt-order-${o.id.slice(0,8)}-track`}
                       className="text-[10px] px-2 py-1 rounded font-black bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/40 flex items-center gap-1">
                      <ExternalLink size={10} /> Track
                    </a>
                    <button onClick={() => setExpanded(isOpen ? null : o.id)}
                            data-testid={`mkt-order-${o.id.slice(0,8)}-expand`}
                            className="text-[10px] px-2 py-1 rounded font-black bg-slate-800 border border-slate-700 hover:bg-slate-700 ml-auto">
                      {isOpen ? '↑ Hide timeline' : '↓ Show timeline'}
                    </button>
                  </div>

                  {/* Expanded timeline */}
                  {isOpen && (
                    <div className="mt-2 p-3 rounded-lg bg-slate-950/60 border border-slate-800" data-testid={`mkt-order-${o.id.slice(0,8)}-timeline`}>
                      <div className="text-[10px] font-black opacity-80 mb-2">📋 Status Timeline</div>
                      <div className="space-y-1.5">
                        {[...(o.tracking_updates || [])].reverse().map((t, i) => (
                          <div key={i} className="flex items-start gap-2 text-[11px]">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                            <div className="flex-1">
                              <span className="font-black">{STATUS_MAP[t.status]?.label || t.status}</span>
                              <span className="opacity-70 ml-2">{t.text}</span>
                              <div className="text-[9px] opacity-50">{new Date(t.at).toLocaleString('en-IN')}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {o.buyer_address && (
                        <div className="mt-2 text-[10px] opacity-80">
                          📍 <b>Delivery address:</b> {o.buyer_address}
                        </div>
                      )}
                      {o.note && (
                        <div className="mt-1 text-[10px] opacity-80">
                          💬 <b>Buyer note:</b> {o.note}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
